import React, { useEffect, useState } from 'react';
import { ConfigurationSection, SpaceBetweenRow } from "@features/settings/pages/StyleComponents";
import { BodyLarge, BodyRegular } from "../../atoms/fonts/Body";
import Colours from "../../atoms/Colours";
import { Switch } from "../../atoms/Switch";
import { useDispatch } from "react-redux";
import { failureNotification } from '@shared/store/notification.duck';
import { SimpleSpinner } from "../../atoms/SimpleSpinner";
import { useTranslation } from "react-i18next";
import { ReportRecipientType, ReportSchedule, ReportType, defaultReportSchedule, scheduleFromConfig } from '@shared/models/report.models';
import { getReportConfigs, updateReportConfig } from '@features/settings/services/ReportService';
import { GenericDropDownList } from "../../molecules/GenericDropDownList";
import { SendSampleReportButton } from "./SendSampleReportButton";
import { ReportScheduleSetting } from "./ReportScheduleSetting";

export function LeaveAndAbsenceDigestSetting() {
  const [isLoading, setIsLoading] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [recipientType, setRecipientType] = useState<ReportRecipientType>(ReportRecipientType.LINE_MANAGERS);
  const [schedule, setSchedule] = useState<ReportSchedule>(defaultReportSchedule(ReportType.LEAVE_ABSENCE_DIGEST));
  const dispatch = useDispatch();
  const { t } = useTranslation();

  useEffect(() => {
    let active = true;
    getReportConfigs()
      .then(configs => {
        if (!active) {
          return;
        }
        const config = configs.find(c => c.reportType === ReportType.LEAVE_ABSENCE_DIGEST);
        if (config) {
          setEnabled(config.enabled);
          if (config.recipientTypes && config.recipientTypes.length > 0) {
            setRecipientType(config.recipientTypes[0]);
          }
          setSchedule(scheduleFromConfig(config, ReportType.LEAVE_ABSENCE_DIGEST));
        }
      })
      .catch(() => { /* leave defaults in place */ });
    return () => { active = false; };
  }, []);

  const persist = async (next: { enabled: boolean; recipientType: ReportRecipientType; schedule: ReportSchedule }) => {
    try {
      setIsLoading(true);
      await updateReportConfig({
        reportType: ReportType.LEAVE_ABSENCE_DIGEST,
        enabled: next.enabled,
        recipientTypes: [next.recipientType],
        ...next.schedule,
      });
      setEnabled(next.enabled);
      setRecipientType(next.recipientType);
      setSchedule(next.schedule);
    } catch (e: any) {
      dispatch(failureNotification('Failed to update setting, please wait a minute.'));
    } finally {
      setIsLoading(false);
    }
  };

  const onToggle = (value: boolean) => persist({ enabled: value, recipientType, schedule });

  const onRecipientChange = (value: ReportRecipientType) => {
    if (value === recipientType) {
      return;
    }
    persist({ enabled, recipientType: value, schedule });
  };

  const onScheduleChange = (value: ReportSchedule) => persist({ enabled, recipientType, schedule: value });

  const recipientItems = [
    { id: ReportRecipientType.LINE_MANAGERS, name: t('settings.leave-digest.recipients.line-managers') },
    { id: ReportRecipientType.TEAM_ADMINS, name: t('settings.leave-digest.recipients.team-admins') },
    { id: ReportRecipientType.ALL_TEAM_MEMBERS, name: t('settings.leave-digest.recipients.all-team-members') },
  ];

  return (
    <ConfigurationSection>
      <SpaceBetweenRow>
        <BodyLarge colour={Colours.blue}>{t('settings.leave-digest.title')}</BodyLarge>
        {isLoading ? <SimpleSpinner size={32} /> : <Switch value={enabled} label="" onChange={onToggle} />}
      </SpaceBetweenRow>
      <BodyRegular style={{ marginBottom: 16 }}>{t('settings.leave-digest.subtitle')}</BodyRegular>
      <SendSampleReportButton reportType={ReportType.LEAVE_ABSENCE_DIGEST} />

      {enabled && (
        <>
          <BodyRegular weight={600} style={{ marginBottom: 8 }}>{t('settings.leave-digest.recipients.label')}</BodyRegular>
          <GenericDropDownList selectedId={recipientType}
                               items={recipientItems}
                               placeholder={'settings.leave-digest.recipients.placeholder'}
                               disabled={isLoading}
                               onSelect={onRecipientChange} />
          <ReportScheduleSetting schedule={schedule}
                                 disabled={isLoading}
                                 noteKey={'settings.leave-digest.schedule-note'}
                                 onChange={onScheduleChange} />
        </>
      )}
    </ConfigurationSection>
  );
}
